/**
 * IFRAP-AI.fr - Console Verbose Page
 * Real-time system logs with 3 modes: Normal, Verbose, Forensic
 * 
 * Design: Apollo 11 cockpit style
 * RÈGLE: Lisibilité > Esthétique
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { value: 'normal', label: 'Normal', description: 'Logs opérationnels essentiels' },
  { value: 'verbose', label: 'Verbose', description: 'Incluant IA et réparations' },
  { value: 'forensic', label: 'Forensic', description: 'Logs exhaustifs pour audit' },
];

const MODULES = [
  { value: '', label: 'Tous modules' },
  { value: 'system', label: 'Système' },
  { value: 'auth', label: 'Auth' },
  { value: 'osint', label: 'OSINT' },
  { value: 'knowledge', label: 'Connaissances' },
  { value: 'kpi', label: 'KPI' },
  { value: 'documents', label: 'Documents' },
  { value: 'notes', label: 'Notes' },
];

const ConsolePage = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('normal');
  const [module, setModule] = useState('');
  const [search, setSearch] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [stats, setStats] = useState(null);
  const logsEndRef = useRef(null);

  // Fetch logs
  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('ifrap_token');
      const params = new URLSearchParams({ mode, limit: '200' });
      if (module) params.append('module', module);
      if (search) params.append('search', search);

      const response = await fetch(`${API}/console/logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('ifrap_token');
      const response = await fetch(`${API}/console/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
    // Poll every 5 seconds
    const interval = setInterval(() => {
      fetchLogs();
      fetchStats();
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, module, search]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  return (
    <div className="h-full flex flex-col space-y-4" data-testid="console-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-mono text-[#33FF00]">Console Verbose</h1>
          <p className="text-gray-500 text-sm">Supervision temps réel du système</p>
        </div>
        
        {/* Stats */}
        {stats && (
          <div className="flex items-center space-x-6 font-mono text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-gray-500">Total:</span>
              <span className="text-white">{stats.total_logs}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-[#FF3333] rounded-full"></span>
              <span className="text-[#FF3333]">{stats.errors_24h}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-[#FFB000] rounded-full"></span>
              <span className="text-[#FFB000]">{stats.warnings_24h}</span>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-4 bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-3">
        {/* Mode selector */}
        <div className="flex items-center space-x-2">
          <span className="text-gray-500 text-sm">Mode:</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-1.5 text-sm font-mono text-white focus:outline-none focus:border-[#FFB000]"
            data-testid="mode-select"
          >
            {MODES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Module filter */}
        <div className="flex items-center space-x-2">
          <span className="text-gray-500 text-sm">Module:</span>
          <select
            value={module}
            onChange={(e) => setModule(e.target.value)}
            className="bg-[#1a1a1a] border border-[#333] rounded px-3 py-1.5 text-sm font-mono text-white focus:outline-none focus:border-[#FFB000]"
            data-testid="module-select"
          >
            {MODULES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans les logs..."
            className="w-full bg-[#1a1a1a] border border-[#333] rounded px-3 py-1.5 text-sm font-mono text-white placeholder-gray-600 focus:outline-none focus:border-[#FFB000]"
            data-testid="search-input"
          />
        </div>

        {/* Auto-scroll toggle */}
        <button
          onClick={() => setAutoScroll(!autoScroll)}
          className={`px-3 py-1.5 rounded text-sm font-mono transition-colors ${
            autoScroll
              ? 'bg-[#33FF00]/20 text-[#33FF00] border border-[#33FF00]/50'
              : 'bg-[#1a1a1a] text-gray-400 border border-[#333]'
          }`}
          data-testid="autoscroll-btn"
        >
          Auto-scroll
        </button>

        {/* Refresh button */}
        <button
          onClick={fetchLogs}
          className="px-3 py-1.5 bg-[#1a1a1a] border border-[#333] rounded text-gray-400 hover:text-white hover:border-[#FFB000] transition-colors"
          data-testid="refresh-btn"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Mode description */}
      <div className="text-xs text-gray-600 font-mono">
        {MODES.find((m) => m.value === mode)?.description}
      </div>

      {/* Logs container */}
      <div 
        className="flex-1 bg-[#050505] border border-[#1a1a1a] rounded-lg overflow-auto font-mono text-sm"
        style={{
          // Subtle scanlines effect (2% opacity, can be disabled)
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.02) 2px, rgba(255,255,255,0.02) 4px)',
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            Chargement des logs...
          </div>
        ) : logs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            Aucun log à afficher
          </div>
        ) : (
          <div className="p-4 space-y-1">
            {logs.map((log, index) => (
              <div
                key={log.id || index}
                className="flex items-start space-x-3 py-1 hover:bg-white/5 rounded px-2 -mx-2 transition-colors"
                data-testid={`log-entry-${index}`}
              >
                {/* Timestamp */}
                <span className="text-gray-600 text-xs whitespace-nowrap">
                  {log.created_at?.split('T')[1]?.slice(0, 12) || '--:--:--'}
                </span>
                
                {/* Level */}
                <span
                  className="text-xs font-bold whitespace-nowrap min-w-[100px]"
                  style={{ color: log.level_color }}
                >
                  [{log.level}]
                </span>
                
                {/* Module */}
                <span className="text-[#00FFFF] text-xs whitespace-nowrap min-w-[80px]">
                  {log.module}
                </span>
                
                {/* Action */}
                <span className="text-gray-400 text-xs whitespace-nowrap">
                  {log.action}
                </span>
                
                {/* Message */}
                <span className="text-gray-300 flex-1">
                  {log.message}
                </span>
                
                {/* Duration if available */}
                {log.duration_ms && (
                  <span className="text-gray-600 text-xs whitespace-nowrap">
                    {log.duration_ms}ms
                  </span>
                )}
              </div>
            ))}
            <div ref={logsEndRef} />
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-gray-600 font-mono">
        <span>{logs.length} logs affichés</span>
        <span>Connecté en tant que {user?.email}</span>
      </div>
    </div>
  );
};

export default ConsolePage;
